import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass } from "@phosphor-icons/react";

/*  Reusable permission-checkbox grid used by both the Role editor and the
    per-employee override page. Renders the SNS Main/Sub layout with parent
    checkboxes that toggle every child (indeterminate on partial). Emits the
    complete Set<string> back to the parent via `onChange`. */
export default function PermissionGrid({ tree = [], value, onChange, testidPrefix = "perm", showToolbar = true, dense = false }) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = q
    ? tree.map((p) => ({ ...p, subs: p.subs.filter((s) => s.label.toLowerCase().includes(q) || p.label.toLowerCase().includes(q)) })).filter((p) => p.subs.length)
    : tree;

  const allIds = useMemo(() => tree.flatMap((p) => p.subs.map((s) => `${p.key}.${s.key}`)), [tree]);
  const total = allIds.length;

  const toggleOne = (id) => {
    const s = new Set(value);
    s.has(id) ? s.delete(id) : s.add(id);
    onChange(s);
  };
  const toggleGroup = (parent) => {
    const s = new Set(value);
    const ids = parent.subs.map((sub) => `${parent.key}.${sub.key}`);
    const allOn = ids.every((id) => s.has(id));
    ids.forEach((id) => (allOn ? s.delete(id) : s.add(id)));
    onChange(s);
  };
  const toggleAll = () => {
    onChange(value.size >= total ? new Set() : new Set(allIds));
  };

  return (
    <div className="space-y-3">
      {showToolbar && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-muted-foreground font-mono" data-testid={`${testidPrefix}-count`}>
            {value.size} of {total} permissions granted
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search permission…"
                data-testid={`${testidPrefix}-search`}
                className="h-9 pl-8 pr-3 text-sm bg-card border border-border rounded-sm w-64"
              />
            </div>
            <Button variant="outline" size="sm" onClick={toggleAll} data-testid={`${testidPrefix}-toggle-all`} className="rounded-sm h-9 text-xs">
              {value.size >= total ? "Uncheck all" : "Check all"}
            </Button>
          </div>
        </div>
      )}

      <div className="border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-[240px_1fr] bg-[hsl(var(--muted))] text-xs uppercase tracking-wide font-mono">
          <div className="py-3 px-4 border-r border-border">Main Permission</div>
          <div className="py-3 px-4">Sub Permission</div>
        </div>
        {filtered.map((parent) => {
          const ids = parent.subs.map((s) => `${parent.key}.${s.key}`);
          const parentChecked = ids.length > 0 && ids.every((id) => value.has(id));
          const parentPartial = !parentChecked && ids.some((id) => value.has(id));
          return (
            <div key={parent.key} className="grid grid-cols-[240px_1fr] border-t border-border" data-testid={`${testidPrefix}-group-${parent.key}`}>
              <label className={`px-4 border-r border-border flex items-start gap-3 text-sm font-medium cursor-pointer select-none ${dense ? "py-3" : "py-4"}`}>
                <input
                  type="checkbox"
                  checked={parentChecked}
                  ref={(el) => { if (el) el.indeterminate = parentPartial; }}
                  onChange={() => toggleGroup(parent)}
                  data-testid={`${testidPrefix}-parent-${parent.key}`}
                  className="mt-0.5"
                />
                <span>{parent.label}</span>
              </label>
              <div className={`px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-6 text-sm ${dense ? "py-2" : "py-3"}`}>
                {parent.subs.map((sub) => {
                  const id = `${parent.key}.${sub.key}`;
                  return (
                    <label key={id} className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={value.has(id)}
                        onChange={() => toggleOne(id)}
                        data-testid={`${testidPrefix}-${id}`}
                      />
                      <span>{sub.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="py-12 text-center text-sm text-muted-foreground">No permissions match "{query}"</div>
        )}
      </div>
    </div>
  );
}
